import React, { useState } from 'react';
import { Search, X, SlidersHorizontal, ChevronDown } from 'lucide-react';
import { Tabs } from './Tabs';
import './FilterBar.css';

/**
 * FilterBar — строка фильтров списка: поиск + 2–3 главных фильтра + «Ещё фильтры»
 * раскрывашкой + чипы активных фильтров (аудит U16).
 *
 *   search / onSearch / searchPlaceholder — строка поиска (нет onSearch — нет поиска)
 *   tabs / activeTab / onTabChange — таблетки-статусы [{ key, label, count? }]
 *   children    — главные фильтры (селекты, период), видны всегда
 *   more        — редкие фильтры: прячутся под кнопку «Ещё фильтры»
 *   moreLabel   — подпись кнопки (по умолчанию «Ещё фильтры»)
 *   moreCount   — сколько редких фильтров включено (число на кнопке)
 *   defaultMoreOpen — раскрыть сразу
 *   moreOpen / onMoreOpenChange — раскрытием управляет страница (например,
 *               помнит выбор в браузере); без них FilterBar помнит сам
 *   chips       — активные фильтры [{ key, label, onRemove }] — строка чипов с крестиком
 *   onReset     — «Сбросить все» рядом с чипами
 *   actions     — кнопки справа («Экспорт», «Обновить»)
 *   plain       — без карточки-рамки (внутри карточки/модалки)
 */
export function FilterBar({
  tabs = [],
  activeTab,
  onTabChange,
  search,
  onSearch,
  searchPlaceholder = 'Поиск…',
  children,
  more,
  moreLabel = 'Ещё фильтры',
  moreCount = 0,
  defaultMoreOpen = false,
  moreOpen: moreOpenProp,
  onMoreOpenChange,
  chips = [],
  onReset,
  actions,
  plain = false,
  className = '',
}) {
  const [moreOpenOwn, setMoreOpenOwn] = useState(defaultMoreOpen);
  const controlled = moreOpenProp !== undefined;
  const moreOpen = controlled ? moreOpenProp : moreOpenOwn;
  const setMoreOpen = (fn) => {
    const next = typeof fn === 'function' ? fn(moreOpen) : fn;
    if (!controlled) setMoreOpenOwn(next);
    if (onMoreOpenChange) onMoreOpenChange(next);
  };
  const hasControls = onSearch || children || more || actions;
  return (
    <div className={`ui-filter-bar ${plain ? 'ui-filter-bar--plain' : ''} ${className}`}>
      {tabs.length > 0 && (
        <Tabs
          variant="pills"
          size="sm"
          className="ui-filter-bar__tabs"
          tabs={tabs}
          activeKey={activeTab}
          onChange={(k) => onTabChange && onTabChange(k)}
        />
      )}

      {hasControls && (
        <div className="ui-filter-bar__controls">
          {onSearch && (
            <div className="ui-filter-bar__search">
              <Search size={16} className="ui-filter-bar__search-icon" aria-hidden="true" />
              <input
                type="text"
                value={search || ''}
                onChange={(e) => onSearch(e.target.value)}
                placeholder={searchPlaceholder}
                aria-label={searchPlaceholder}
                className="ui-filter-bar__search-input"
              />
              {search && (
                <button type="button" className="ui-filter-bar__search-clear" onClick={() => onSearch('')}
                        aria-label="Очистить поиск" title="Очистить поиск">
                  <X size={14} />
                </button>
              )}
            </div>
          )}
          {children && <div className="ui-filter-bar__extra">{children}</div>}
          {more && (
            <button
              type="button"
              className={`ui-filter-bar__more-btn ${moreOpen ? 'is-open' : ''}`}
              onClick={() => setMoreOpen(v => !v)}
              aria-expanded={moreOpen}
            >
              <SlidersHorizontal size={15} aria-hidden="true" />
              <span>{moreLabel}</span>
              {moreCount > 0 && <span className="ui-filter-bar__more-count">{moreCount}</span>}
              <ChevronDown size={14} className="ui-filter-bar__more-chevron" aria-hidden="true" />
            </button>
          )}
          {actions && <div className="ui-filter-bar__actions">{actions}</div>}
        </div>
      )}

      {more && moreOpen && <div className="ui-filter-bar__more">{more}</div>}

      {chips.length > 0 && (
        <div className="ui-filter-bar__chips">
          {chips.map(ch => (
            <span key={ch.key} className="ui-filter-bar__chip">
              {ch.label}
              {ch.onRemove && (
                <button type="button" onClick={ch.onRemove} aria-label={`Убрать фильтр: ${ch.label}`}>
                  <X size={12} />
                </button>
              )}
            </span>
          ))}
          {onReset && (
            <button type="button" className="ui-filter-bar__reset" onClick={onReset}>Сбросить все</button>
          )}
        </div>
      )}
    </div>
  );
}
